
// File: src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
// Using a custom button below for custom hover effects
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import logo from '/vixalor-logo.webp';
import heroLogo from '/vixalor-login-logo.webp';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  // Set page title
  useEffect(() => {
    document.title = 'Vixalor POS - Login';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsLoading(true);
  try {
    await login(email, password);
  } catch (err: any) {
    toast.error(err.message || 'Login failed');
    console.error('Login.tsx error:', err);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-[#e7eef7] flex items-center justify-center px-4 py-10">
      <div className="relative w-full max-w-6xl overflow-hidden rounded-3xl shadow-2xl border border-white/60 bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left hero */}
          <div className="relative text-white px-10 py-12 flex flex-col gap-8 overflow-hidden"
               style={{background: 'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.12), transparent 38%), radial-gradient(circle at 78% 8%, rgba(255,255,255,0.08), transparent 32%), linear-gradient(135deg, #0b2f73 0%, #0a2f63 45%, #0a3c8f 100%)'}}>
            <div className="absolute inset-0 pointer-events-none" />
            {/* Glow behind logo */}
            <div className="absolute left-8 top-10 h-80 w-80 rounded-full bg-cyan-200/30 blur-[130px]" />
            <div className="absolute -40 -30 h-96 w-96 rounded-full bg-sky-500/25 blur-[140px]" />

            {/* Animated coins */}
            <div className="absolute right-10 top-16 coin" />
            <div className="absolute right-24 top-32 coin delay-150" />
            <div className="absolute right-36 top-10 coin delay-300" />

            <div className="relative flex items-center gap-4">
              <img src={heroLogo} alt="Vixalor" className="h-24 w-auto drop-shadow-[0_16px_45px_rgba(0,0,0,0.45)]" />
            </div>
            <div className="relative space-y-4">
              <p className="text-4xl font-semibold leading-tight tracking-tight">Powering Smarter Retail Operations</p>
              <ul className="space-y-3 text-lg text-white/90">
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white text-sm shadow-lg">✓</span>
                  Secure
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white text-sm shadow-lg">✓</span>
                  Cloud Based
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white text-sm shadow-lg">✓</span>
                  Real-time Analytics
                </li>
              </ul>
            </div>
          </div>

          {/* Right form */}
          <div className="p-8 lg:p-12 bg-white">

            <Card className="border-none shadow-none">
              <CardHeader className="px-0">
                <CardTitle className="text-3xl font-semibold text-slate-900">Sign In</CardTitle>
                <CardDescription className="text-base text-slate-600">
                  Enter your credentials to access your dashboard.
                </CardDescription>
              </CardHeader>

              <form onSubmit={handleSubmit}>
                <CardContent className="space-y-6 px-0">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        autoComplete="username"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={isLoading}
                        required
                        className="pl-12 h-12 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#0f3d8a]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        disabled={isLoading}
                        required
                        className="pl-12 pr-14 h-12 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-[#0f3d8a]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="px-0 pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={[
                      'w-full h-12 rounded-xl font-semibold text-white tracking-wide',
                      'bg-gradient-to-r from-[#0a2a5f] via-[#0f3d8a] to-[#0a2a5f]',
                      'shadow-[0_10px_30px_rgba(15,61,138,0.35)] relative overflow-hidden',
                      'transition-all duration-200 ease-out',
                      'hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,61,138,0.45)]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0f3d8a] focus-visible:ring-offset-white',
                      isLoading ? 'opacity-90 cursor-progress' : 'cursor-pointer'
                    ].join(' ')}
                  >
                    <span className="absolute inset-0 pointer-events-none ripple" />
                    {isLoading ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing In...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span>Sign In</span>
                        <span className="text-lg leading-none">→</span>
                      </div>
                    )}
                  </button>
                </CardFooter>
              </form>
            </Card>
          </div>
        </div>
      </div>
      <style>{`
        .coin {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, #ffe8a3, #f2a100 65%);
          box-shadow: 0 8px 20px rgba(0,0,0,0.25);
          animation: coinFloat 4s ease-in-out infinite;
        }
        .coin.delay-150 { animation-delay: 0.15s; }
        .coin.delay-300 { animation-delay: 0.3s; }
        @keyframes coinFloat {
          0% { transform: translateY(0) rotate(-4deg); opacity: 0.9; }
          50% { transform: translateY(-12px) rotate(4deg); opacity: 1; }
          100% { transform: translateY(0) rotate(-4deg); opacity: 0.9; }
        }
        .ripple::before, .ripple::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background: radial-gradient(circle, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 60%);
          transform: scale(0);
          opacity: 0;
          animation: rippleWave 2s infinite;
        }
        .ripple::after {
          animation-delay: 1s;
        }
        @keyframes rippleWave {
          0% { transform: scale(0); opacity: 0.35; }
          60% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.8); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
